import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography
} from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight, Search, CheckCircle } from '@mui/icons-material';
import { headingBlue, lightGrey, maroon, mediumGrey, pureWhite, textGeen, orange } from '../../../assets/colors';
import { buttonStyle } from '../../../utils/commonCSS';
import { getUncategorisedWORKers, updateWORKerProfile } from '../../../backend/backendApis';
import HttpService from '../../../backend/requestApis';
import _ from 'lodash';

const UncategorisedWORKersTable = (props) => {
  const [activeTab, setActiveTab] = useState('uncategorized');
  const [workersList, setWorkersList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [selectedCategories, setSelectedCategories] = useState({});
  const [updatingContact, setUpdatingContact] = useState(null);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const getWorkerCategory = (worker) => {
    const cat = worker?.WORKerCateogary || worker?.WORKerCategory || worker?.workerCategory || worker?.workerCateogary;
    if (!cat || typeof cat !== 'string') return '';
    const cleaned = cat.trim().toLowerCase().replace(/[^a-z]/g, '');
    if (cleaned === 'committed') return 'committed';
    if (cleaned === 'noncommitted' || cleaned === 'noncommited' || cleaned === 'uncommitted') return 'nonCommitted';
    return '';
  };

  const fetchList = useCallback(
    (searchVal, pageNum, tabValue) => {
      setLoader(true);
      const currentTab = tabValue !== undefined ? tabValue : activeTab;
      const queryParams = {
        page: pageNum || page,
        offset: rowsPerPage
      };
      if (currentTab === 'committed') {
        queryParams.WORKerCategory = 'committed';
      } else if (currentTab === 'uncommitted') {
        queryParams.WORKerCategory = 'uncommitted';
      }
      if (searchVal && searchVal.trim() !== '') {
        queryParams.searchText = searchVal.trim();
      }
      if (props.chartFilterDistrict) {
        queryParams.district = props.chartFilterDistrict;
      }
      if (props.chartFilterState) {
        queryParams.chapter = props.chartFilterState;
      }
      if (props.chartFilterGender) {
        queryParams.gender = props.chartFilterGender;
      }

      const options = {
        url: getUncategorisedWORKers,
        method: 'GET',
        params: queryParams,
        headers: {
          Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
        }
      };

      HttpService(options)
        .then((resp) => {
          setWorkersList(resp?.data?.data || []);
          setLoader(false);
        })
        .catch((err) => {
          console.log('Error fetching workers:', err);
          setWorkersList([]);
          setLoader(false);
        });
    },
    [page, rowsPerPage, activeTab, props.chartFilterDistrict, props.chartFilterState, props.chartFilterGender]
  );

  const debouncedSearch = useCallback(
    _.debounce((value) => {
      setPage(1);
      fetchList(value, 1, activeTab);
    }, 400),
    [fetchList, activeTab]
  );

  useEffect(() => {
    fetchList(searchText, page, activeTab);
  }, [page, activeTab, props.chartFilterDistrict, props.chartFilterState, props.chartFilterGender, props.refreshTrigger]);

  const handleTabChange = (event, newTab) => {
    setActiveTab(newTab);
    setPage(1);
    setSelectedCategories({});
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    debouncedSearch(val);
  };

  const handleCategoryChange = (worker, val) => {
    const initialCategory = getWorkerCategory(worker);
    setSelectedCategories((prev) => {
      const copy = { ...prev };
      if (val === initialCategory) {
        delete copy[worker.contactNumber];
      } else {
        copy[worker.contactNumber] = val;
      }
      return copy;
    });
  };

  const handleUpdateCategory = async (worker) => {
    const chosenCategory = selectedCategories[worker.contactNumber];
    if (!chosenCategory) return;

    setUpdatingContact(worker.contactNumber);
    try {
      const options = {
        url: updateWORKerProfile,
        method: 'POST',
        payload: {
          contactNumber: worker.contactNumber,
          workerType: worker.WORKerType,
          workerCategory: chosenCategory
        },
        headers: {
          headers: {
            Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : ''
          }
        }
      };
      await HttpService(options);
      setUpdatingContact(null);

      setSelectedCategories((prev) => {
        const copy = { ...prev };
        delete copy[worker.contactNumber];
        return copy;
      });

      setSuccessMsg(`Updated ${worker.name || worker.contactNumber} successfully!`);
      setTimeout(() => setSuccessMsg(''), 3000);

      fetchList(searchText, page, activeTab);
      if (props.refreshPieChart) {
        props.refreshPieChart();
      }
    } catch (err) {
      console.log('Error updating worker profile:', err);
      setUpdatingContact(null);
      setErrorMsg('Failed to update WORKer category.');
      setTimeout(() => setErrorMsg(''), 3000);
    }
  };

  const selectedCount = Object.keys(selectedCategories).length;

  const handleUpdateAllSelected = async () => {
    const contacts = Object.keys(selectedCategories);
    if (contacts.length === 0) return;

    setIsBulkUpdating(true);
    try {
      for (const contact of contacts) {
        const worker = workersList.find((w) => w.contactNumber === contact);
        if (worker && selectedCategories[contact]) {
          const options = {
            url: updateWORKerProfile,
            method: 'POST',
            payload: {
              contactNumber: worker.contactNumber,
              workerType: worker.WORKerType,
              workerCategory: selectedCategories[contact]
            },
            headers: {
              headers: {
                Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : ''
              }
            }
          };
          await HttpService(options);
        }
      }
      setIsBulkUpdating(false);
      setSelectedCategories({});
      setSuccessMsg('Successfully updated selected WORKers!');
      setTimeout(() => setSuccessMsg(''), 3000);
      fetchList(searchText, page, activeTab);
      if (props.refreshPieChart) {
        props.refreshPieChart();
      }
    } catch (err) {
      console.log('Error in bulk updating workers:', err);
      setIsBulkUpdating(false);
      setErrorMsg('Failed to update some WORKers.');
      setTimeout(() => setErrorMsg(''), 3000);
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header bar with Tabs */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          px: 1,
          pt: 0.5,
          borderBottom: '1px solid #ECECEC'
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            minHeight: '36px',
            '& .MuiTabs-flexContainer': {
              gap: 0.5
            },
            '& .MuiTab-root': {
              minHeight: '36px',
              py: 0.5,
              px: { xs: 1, sm: 1.5 },
              fontSize: { xs: '0.78rem', sm: '0.82rem' },
              fontWeight: 600,
              textTransform: 'none',
              color: '#666',
              '&.Mui-selected': {
                color: headingBlue
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: headingBlue,
              height: 2.5
            }
          }}
        >
          <Tab label="Uncategorized" value="uncategorized" />
          <Tab label="Committed WORKers" value="committed" />
          <Tab label="Supported WORKer" value="uncommitted" />
        </Tabs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
          {selectedCount > 1 && (
            <Button
              size="small"
              onClick={handleUpdateAllSelected}
              disabled={isBulkUpdating}
              style={{
                ...buttonStyle,
                fontSize: '0.78rem',
                padding: '0.15rem 0.6rem',
                backgroundColor: headingBlue
              }}
            >
              {isBulkUpdating ? (
                <CircularProgress size={14} sx={{ color: pureWhite }} />
              ) : (
                `Update Selected (${selectedCount})`
              )}
            </Button>
          )}
          <TextField
            size="small"
            placeholder="Search Name / Phone"
            value={searchText}
            onChange={handleSearchChange}
            sx={{
              width: { xs: 150, sm: 190 },
              '& .MuiInputBase-input': {
                py: 0.4,
                px: 1,
                fontSize: '0.8rem'
              }
            }}
          />
        </Box>
      </Box>

      {/* Success / Error notification strip */}
      {(successMsg || errorMsg) && (
        <Box sx={{ px: 1.5, py: 0.4, display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: successMsg ? '#edf7ed' : '#fdeded' }}>
          {successMsg && (
            <Typography sx={{ color: '#2e7d32', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircle sx={{ fontSize: '0.9rem' }} /> {successMsg}
            </Typography>
          )}
          {errorMsg && (
            <Typography sx={{ color: maroon, fontSize: '0.78rem' }}>
              {errorMsg}
            </Typography>
          )}
        </Box>
      )}

      {/* Table Container */}
      <TableContainer
        sx={{
          maxHeight: 300,
          minHeight: 240,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none'
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: headingBlue, fontWeight: 600, fontSize: '0.8rem', py: 0.8, backgroundColor: lightGrey }}>
                WORKer
              </TableCell>
              <TableCell sx={{ color: headingBlue, fontWeight: 600, fontSize: '0.8rem', py: 0.8, backgroundColor: lightGrey }}>
                Contact
              </TableCell>
              <TableCell sx={{ color: headingBlue, fontWeight: 600, fontSize: '0.8rem', py: 0.8, backgroundColor: lightGrey }}>
                District
              </TableCell>
              <TableCell sx={{ color: headingBlue, fontWeight: 600, fontSize: '0.8rem', py: 0.8, backgroundColor: lightGrey, textAlign: 'center', minWidth: 230 }}>
                Classification
              </TableCell>
              <TableCell sx={{ color: headingBlue, fontWeight: 600, fontSize: '0.8rem', py: 0.8, backgroundColor: lightGrey, textAlign: 'center' }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loader ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 5 }}>
                  <CircularProgress size={28} sx={{ color: headingBlue }} />
                </TableCell>
              </TableRow>
            ) : workersList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 5, color: '#888', fontSize: '0.88rem' }}>
                  {activeTab === 'committed'
                    ? 'No Committed WORKers found.'
                    : activeTab === 'uncommitted'
                      ? 'No Supported WORKers found.'
                      : 'No uncategorised WORKers found.'}
                </TableCell>
              </TableRow>
            ) : (
              workersList.map((worker) => {
                const initialCategory = getWorkerCategory(worker);
                const userChosenCategory = selectedCategories[worker.contactNumber];
                const displayedCategory = userChosenCategory !== undefined ? userChosenCategory : initialCategory;
                const hasChanged = userChosenCategory !== undefined && userChosenCategory !== initialCategory;
                const isRowUpdating = updatingContact === worker.contactNumber;

                return (
                  <TableRow key={worker._id || worker.contactNumber} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ fontSize: '0.82rem', py: 0.7, fontWeight: 500 }}>
                      {worker.name || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', py: 0.7, color: '#555' }}>
                      {worker.contactNumber || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', py: 0.7, color: '#555' }}>
                      {worker.district ? `${worker.district}` : '-'}
                    </TableCell>
                    <TableCell sx={{ py: 0.4, minWidth: 230 }}>
                      <RadioGroup
                        row
                        value={displayedCategory}
                        onChange={(e) => handleCategoryChange(worker, e.target.value)}
                        sx={{ justifyContent: 'center', flexWrap: 'nowrap', gap: { xs: 1.5, sm: 2.5 } }}
                      >
                        <FormControlLabel
                          value="committed"
                          control={<Radio size="small" sx={{ color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' }, p: 0.4 }} />}
                          label={<Typography sx={{ fontSize: '0.78rem', color: '#2e7d32', fontWeight: 500, ml: 0.3 }}>Committed WORKers</Typography>}
                          sx={{ mr: 0 }}
                        />
                        <FormControlLabel
                          value="nonCommitted"
                          control={<Radio size="small" sx={{ color: '#ed683c', '&.Mui-checked': { color: '#ed683c' }, p: 0.4 }} />}
                          label={<Typography sx={{ fontSize: '0.78rem', color: '#ed683c', fontWeight: 500, ml: 0.3 }}>Supported WORKer</Typography>}
                          sx={{ mr: 0 }}
                        />
                      </RadioGroup>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', py: 0.4 }}>
                      <Button
                        size="small"
                        disabled={!hasChanged || isRowUpdating}
                        onClick={() => handleUpdateCategory(worker)}
                        style={{
                          ...buttonStyle,
                          fontSize: '0.74rem',
                          padding: '0.15rem 0.55rem',
                          marginRight: 0,
                          backgroundColor: !hasChanged ? mediumGrey : headingBlue,
                          border: 'none',
                          cursor: !hasChanged ? 'not-allowed' : 'pointer',
                          minWidth: '3.6rem'
                        }}
                      >
                        {isRowUpdating ? (
                          <CircularProgress size={14} sx={{ color: pureWhite }} />
                        ) : (
                          'Update'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Footer */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1.5,
          py: 0.4,
          borderTop: '1px solid #ECECEC',
          backgroundColor: '#fafafa',
          borderRadius: '0 0 0.5rem 0.5rem'
        }}
      >
        <Typography sx={{ color: '#777', fontSize: '0.78rem' }}>
          Showing {workersList.length} items
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <KeyboardArrowLeft sx={{ color: page === 1 ? mediumGrey : headingBlue, fontSize: '1.2rem' }} />
          </IconButton>
          <Typography sx={{ fontSize: '0.8rem', color: headingBlue, px: 0.8, fontWeight: 500 }}>
            Page {page}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPage((p) => p + 1)}
            disabled={workersList.length < rowsPerPage}
          >
            <KeyboardArrowRight sx={{ color: workersList.length < rowsPerPage ? mediumGrey : headingBlue, fontSize: '1.2rem' }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default UncategorisedWORKersTable;
